import React, { useEffect, useState } from "react";
import { EnvelopeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { PageLoader, TabNavigation, Button } from "../../ui";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptStatusBadge from "../components/VaptStatusBadge";
import VaptNotificationPreviewModal from "../components/VaptNotificationPreviewModal";
import VaptCoverageExceptionsPanel from "../components/VaptCoverageExceptionsPanel";
import {
  getVaptReportsApi,
  generateVaptReportApi,
  listVaptExecutionCoverageApi,
  listVaptToolExecutionsApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { getVaptErrorMessage } from "../utils/apiError";
import { VAPT_SEVERITIES } from "../utils/vaptConstants";
import { DataTable } from "../../common/DataTable";

// Report views built from real backend data (dashboard + findings + scans).
// No PDF/export generation exists on the backend, and there is no
// narrative-generation model for an executive summary, so those gaps are
// shown as explicit "not available" notes rather than fabricated text.
const VaptReports = () => {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("executive");
  // Shape returned by listVaptExecutionCoverageApi:
  //   { summary: {...totals}, projects: [{ project_id, project_name, summary, rows }] }
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState(null);
  // scan_id -> real ToolExecution rows (the only proof a tool actually ran).
  const [executionsByScan, setExecutionsByScan] = useState({});
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [executionsError, setExecutionsError] = useState(null);
  const [showNotificationPreview, setShowNotificationPreview] = useState(false);
  const [notice, setNotice] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    Promise.all([
      getVaptReportsApi(),
      // A coverage failure must not blank the whole Reports page — it is
      // reported inside the tabs that use it.
      listVaptExecutionCoverageApi().catch((err) => {
        setCoverageError(getVaptErrorMessage(err, "Failed to load execution coverage."));
        return null;
      }),
    ])
      .then(([data, cov]) => {
        setReports(data);
        setCoverage(cov);
        setLoading(false);
      })
      .catch((err) => {
        setError(getVaptErrorMessage(err, "Failed to load reports."));
        setLoading(false);
      });
  }, []);

  const scanIdsKey = (reports?.scanExecution || []).map((s) => s.scan_id).join(",");

  // Load real tool-execution rows for each scan, only while the Execution
  // Report tab is open.
  useEffect(() => {
    if (tab !== "execution" || !scanIdsKey) return undefined;
    let mounted = true;
    setExecutionsLoading(true);
    setExecutionsError(null);
    Promise.all(
      scanIdsKey.split(",").map((scanId) =>
        listVaptToolExecutionsApi(scanId).then((rows) => [scanId, Array.isArray(rows) ? rows : []])
      )
    )
      .then((pairs) => {
        if (mounted) setExecutionsByScan(Object.fromEntries(pairs));
      })
      .catch((err) => {
        if (mounted) setExecutionsError(getVaptErrorMessage(err, "Failed to load tool executions."));
      })
      .finally(() => {
        if (mounted) setExecutionsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [tab, scanIdsKey]);

  const handleGenerate = async (tabId) => {
    const reportType = REPORT_TYPE_BY_TAB[tabId];
    const tabName = tabs.find((t) => t.id === tabId)?.name || "Report";

    if (!reportType) {
      // No matching backend report_type for this tab — stays a local,
      // honest notice rather than calling an endpoint that would reject it.
      setNotice(`${tabName} is a summary view and is not available as an exported report.`);
      return;
    }

    // Report.project is a required ReferenceField on the backend — it needs
    // the project's real Mongo id (the same raw value VulnerabilityScan's/
    // VAPTFinding's own `project` field already carries), not the human
    // project_id business key. Derive it from whatever real data is already
    // loaded on this page; if none exists yet, there is nothing to attach
    // the report to.
    const projectRef =
      reports.scanExecution?.[0]?.project ||
      reports.findings?.[0]?.project ||
      reports.risk?.[0]?.project ||
      null;

    if (!projectRef) {
      setNotice("No engagement with scan or finding data is available to generate this report.");
      return;
    }

    setGenerating(true);
    try {
      const created = await generateVaptReportApi({ project: projectRef, report_type: reportType, file_format: "HTML" });
      const data = await getVaptReportsApi();
      setReports(data);
      setNotice(
        created?.status === "Generated"
          ? `${tabName} report generated (${created.report_id}). Download it from the Reports tab on the Engagement Details page.`
          : `${tabName} report record created; file generation ${created?.status === "Failed" ? "failed" : "is in progress"}. Check the Reports tab on the Engagement Details page.`
      );
    } catch (err) {
      setNotice(getVaptErrorMessage(err, "Failed to generate report."));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <PageLoader title="Loading reports…" />;
  if (error || !reports) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">{error || "No report data available."}</p>
      </div>
    );
  }

  const tabs = [
    { id: "executive", name: "Executive" },
    { id: "technical", name: "Technical" },
    { id: "findings", name: "Findings" },
    { id: "risk", name: "Risk" },
    { id: "scanExecution", name: "Scan Execution" },
    { id: "execution", name: "Execution Report" },
    { id: "retest", name: "Retest" },
    { id: "coverage", name: "Coverage & Exceptions" },
  ];

  // The backend Report model's report_type is a fixed choice field
  // ('Executive'|'Technical'|'Findings'|'Risk'|'Scan Execution'|'Retest') —
  // "Execution Report" and "Coverage & Exceptions" are frontend-only tabs
  // with no matching backend report_type, so Generate Report is a local
  // notice for those instead of a real API call (was previously always
  // calling the API with an invalid/missing report_type and no project,
  // which the backend correctly rejected with a 400).
  const REPORT_TYPE_BY_TAB = {
    executive: "Executive",
    technical: "Technical",
    findings: "Findings",
    risk: "Risk",
    scanExecution: "Scan Execution",
    retest: "Retest",
  };

  const findingsRows = (reports.findings || []).map((f) => ({ ...f, id: f.finding_id }));
  const riskRows = (reports.risk || []).map((f) => ({
    ...f,
    id: f.finding_id,
    status: f.validation_status === "Accepted Risk" ? "Accepted" : "Open",
  }));
  const scanRows = (reports.scanExecution || []).map((s) => ({ ...s, id: s.scan_id }));
  const executionProjects = Array.isArray(coverage?.projects) ? coverage.projects : [];
  const retestRows = (reports.retest || []).map((f) => ({ ...f, id: f.finding_id }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Reports</h2>
          <p className="text-sm text-gray-500">
            Reports and coverage views are generated from VAPT scan results, findings, risk, remediation, and scope data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={PlusIcon} loading={generating} onClick={() => handleGenerate(tab)}>
            Generate Report
          </Button>
          <Button variant="secondary" icon={EnvelopeIcon} onClick={() => setShowNotificationPreview(true)}>
            Customer Notification Preview
          </Button>
        </div>
      </div>

      {notice && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-4 py-2.5">{notice}</div>
      )}

      <TabNavigation tabs={tabs} activeTab={tab} onTabChange={setTab} boxedActive />

      {tab === "executive" && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Executive Summary</h3>
            {reports.executive.summary ? (
              <p className="text-sm text-gray-700">{reports.executive.summary}</p>
            ) : (
              <p className="text-sm text-gray-400">
                No executive summary narrative is available for this report.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(reports.executive.keyMetrics).map(([k, v]) => (
              <div key={k} className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-xs text-gray-400 capitalize">{k.replace(/([A-Z])/g, " $1")}</p>
                <p className="text-xl font-bold text-gray-800">{v ?? "—"}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "technical" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(reports.technical.bySeverity).map(([sev, count]) => (
              <div key={sev} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-gray-800">{count}</p>
                <VaptSeverityBadge severity={sev} size="sm" />
              </div>
            ))}
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Top Findings (by CVSS score)</h3>
          <DataTable
            columns={[
              {
                id: "title",
                accessorFn: (r) => r.title,
                header: "Finding",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
                cell: ({ row }) => <span>{row.original.title}</span>,
              },
              {
                id: "severity",
                accessorFn: (r) => r.severity,
                header: "Severity",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Severity",
                    placeholder: "All severities",
                    options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <VaptSeverityBadge severity={row.original.severity} size="sm" />,
              },
              {
                id: "cvss_score",
                accessorFn: (r) => r.cvss_score,
                header: "CVSS",
                enableSorting: false,
                cell: ({ row }) => <span>{row.original.cvss_score ?? "—"}</span>,
              },
              {
                id: "target_display",
                accessorFn: (r) => r.target_display,
                header: "Target",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Target", placeholder: "Search target" } },
                cell: ({ row }) => <span>{row.original.target_display ?? "—"}</span>,
              },
            ]}
            data={(reports.technical.topFindings || []).map((f) => ({ ...f, id: f.finding_id }))}
            getRowId={(r) => String(r.id)}
            emptyMessage="No findings recorded."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      {tab === "findings" && (
        <DataTable
          columns={[
            {
              id: "title",
              accessorFn: (r) => r.title,
              header: "Finding",
              enableSorting: false,
              meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
              cell: ({ row }) => <span>{row.original.title}</span>,
            },
            {
              id: "severity",
              accessorFn: (r) => r.severity,
              header: "Severity",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Severity",
                  placeholder: "All severities",
                  options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <VaptSeverityBadge severity={row.original.severity} size="sm" />,
            },
            {
              id: "validation_status",
              accessorFn: (r) => r.validation_status,
              header: "Validation",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Validation",
                  placeholder: "All validation statuses",
                  options: [...new Set(findingsRows.map((r) => r.validation_status).filter(Boolean))].map((v) => ({
                    value: v,
                    label: v,
                  })),
                },
              },
              cell: ({ row }) => <VaptStatusBadge status={row.original.validation_status} size="sm" />,
            },
            {
              id: "finding_status",
              accessorFn: (r) => r.finding_status,
              header: "Remediation",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Remediation",
                  placeholder: "All remediation statuses",
                  options: [...new Set(findingsRows.map((r) => r.finding_status).filter(Boolean))].map((v) => ({
                    value: v,
                    label: v,
                  })),
                },
              },
              cell: ({ row }) => <VaptStatusBadge status={row.original.finding_status} size="sm" />,
            },
          ]}
          data={findingsRows}
          getRowId={(r) => String(r.id)}
          emptyMessage="No findings recorded."
          enableColumnVisibility={false}
          enableColumnResizing
          enableColumnFilters
          maxBodyHeight="65vh"
        />
      )}

      {tab === "risk" && (
        <DataTable
          columns={[
            {
              id: "title",
              accessorFn: (r) => r.title,
              header: "Risk",
              enableSorting: false,
              meta: { filter: { variant: "text", label: "Risk", placeholder: "Search risk" } },
              cell: ({ row }) => <span>{row.original.title}</span>,
            },
            {
              id: "severity",
              accessorFn: (r) => r.severity,
              header: "Severity",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Severity",
                  placeholder: "All severities",
                  options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <VaptSeverityBadge severity={row.original.severity} size="sm" />,
            },
            {
              id: "status",
              accessorFn: (r) => r.status,
              header: "Status",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Status",
                  placeholder: "All statuses",
                  options: [
                    { value: "Open", label: "Open" },
                    { value: "Accepted", label: "Accepted" },
                  ],
                },
              },
              cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
            },
          ]}
          data={riskRows}
          getRowId={(r) => String(r.id)}
          emptyMessage="No findings recorded."
          enableColumnVisibility={false}
          enableColumnResizing
          enableColumnFilters
          maxBodyHeight="65vh"
        />
      )}

      {tab === "scanExecution" && (
        <DataTable
          columns={[
            {
              id: "scan_id",
              accessorFn: (r) => r.scan_id,
              header: "Scan ID",
              enableSorting: false,
              meta: { filter: { variant: "text", label: "Scan ID", placeholder: "Search scan ID" } },
              cell: ({ row }) => <span>{row.original.scan_id}</span>,
            },
            {
              id: "project",
              accessorFn: (r) => r.project_name || r.project_display_id || "Asset VAPT Project",
              header: "Project",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Project",
                  placeholder: "All projects",
                  options: [
                    ...new Set(
                      scanRows.map((r) => r.project_name || r.project_display_id || "Asset VAPT Project").filter(Boolean)
                    ),
                  ].map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <span>{row.original.project_name || row.original.project_display_id || "Asset VAPT Project"}</span>,
            },
            {
              id: "scan_type",
              accessorFn: (r) => r.scan_type,
              header: "Type",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Type",
                  placeholder: "All types",
                  options: [...new Set(scanRows.map((r) => r.scan_type).filter(Boolean))].map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <span>{row.original.scan_type ?? "—"}</span>,
            },
            {
              id: "status",
              accessorFn: (r) => r.status,
              header: "Status",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Status",
                  placeholder: "All statuses",
                  options: [...new Set(scanRows.map((r) => r.status).filter(Boolean))].map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
            },
            {
              id: "vulnerabilities_found",
              accessorFn: (r) => r.vulnerabilities_found,
              header: "Findings",
              enableSorting: false,
              cell: ({ row }) => <span>{row.original.vulnerabilities_found ?? "—"}</span>,
            },
          ]}
          data={scanRows}
          getRowId={(r) => String(r.id)}
          emptyMessage="No scans recorded."
          enableColumnVisibility={false}
          enableColumnResizing
          enableColumnFilters
          maxBodyHeight="65vh"
        />
      )}

      {tab === "execution" && (
        <div className="space-y-4">
          <p className="text-xs text-gray-400">
            Execution report — Total / Applicable / Scanned / Failed / Unsupported / Out-of-Scope, plus a
            configured-vs-executed tools breakdown per engagement.
          </p>
          {coverageError && (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">
              {coverageError}
            </div>
          )}
          {!coverageError && executionProjects.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <p className="text-gray-500">No execution coverage data recorded.</p>
            </div>
          ) : (
            executionProjects.map((proj) => {
              const s = proj.summary || {};
              return (
                <div key={proj.project_id} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {proj.project_name || proj.project_id} <span className="text-xs text-gray-400">({proj.project_id})</span>
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                    {[
                      ["Total", s.total],
                      ["Applicable", s.applicable],
                      ["Scanned", s.implemented],
                      ["Not scanned", s.not_implemented],
                      ["Unsupported", s.unsupported],
                      ["Out of Scope", s.out_of_scope],
                      ["Blocked", s.blocked],
                    ].map(([label, value]) => (
                      <div key={label} className="border border-gray-100 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-gray-400 uppercase">{label}</p>
                        <p className="text-lg font-bold text-gray-800">{value ?? "—"}</p>
                      </div>
                    ))}
                  </div>
                  <DataTable
                    columns={[
                      {
                        id: "target",
                        accessorFn: (r) => r.target,
                        header: "Target",
                        enableSorting: false,
                        meta: { filter: { variant: "text", label: "Target", placeholder: "Search target" } },
                        cell: ({ row }) => <span>{row.original.target}</span>,
                      },
                      {
                        id: "domain",
                        accessorFn: (r) => r.domain,
                        header: "Domain",
                        enableSorting: false,
                        meta: { filter: { variant: "text", label: "Domain", placeholder: "Search domain" } },
                        cell: ({ row }) => <span>{row.original.domain ?? "—"}</span>,
                      },
                      {
                        id: "coverage_status",
                        accessorFn: (r) => r.coverage_status,
                        header: "Coverage",
                        enableSorting: false,
                        meta: {
                          filter: {
                            variant: "select",
                            label: "Coverage",
                            placeholder: "All statuses",
                            options: [
                              ...new Set((Array.isArray(proj.rows) ? proj.rows : []).map((r) => r.coverage_status).filter(Boolean)),
                            ].map((v) => ({ value: v, label: v })),
                          },
                        },
                        cell: ({ row }) => <VaptStatusBadge status={row.original.coverage_status} size="sm" />,
                      },
                      {
                        id: "target_status",
                        accessorFn: (r) => r.target_status,
                        header: "Target Status",
                        enableSorting: false,
                        meta: {
                          filter: {
                            variant: "select",
                            label: "Target Status",
                            placeholder: "All statuses",
                            options: [
                              ...new Set((Array.isArray(proj.rows) ? proj.rows : []).map((r) => r.target_status).filter(Boolean)),
                            ].map((v) => ({ value: v, label: v })),
                          },
                        },
                        cell: ({ row }) => <span>{row.original.target_status ?? "—"}</span>,
                      },
                    ]}
                    data={(Array.isArray(proj.rows) ? proj.rows : []).map((r, i) => ({ ...r, id: i }))}
                    getRowId={(r) => String(r.id)}
                    emptyMessage="No targets recorded for this engagement."
                    enableColumnVisibility={false}
                    enableColumnResizing
                    enableColumnFilters
                    maxBodyHeight="65vh"
                  />
                </div>
              );
            })
          )}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Configured vs Executed Tools (by scan)</h3>
            {executionsError && (
              <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5 mb-2">
                {executionsError}
              </div>
            )}
            {executionsLoading && <p className="text-xs text-gray-400 mb-2">Loading tool executions…</p>}
            <DataTable
              columns={[
                {
                  id: "scan_id",
                  accessorFn: (r) => r.scan_id,
                  header: "Scan ID",
                  enableSorting: false,
                  meta: { filter: { variant: "text", label: "Scan ID", placeholder: "Search scan ID" } },
                  cell: ({ row }) => <span>{row.original.scan_id}</span>,
                },
                {
                  id: "project",
                  accessorFn: (r) => r.project_name || r.project_display_id || "—",
                  header: "Project",
                  enableSorting: false,
                  meta: {
                    filter: {
                      variant: "select",
                      label: "Project",
                      placeholder: "All projects",
                      options: [
                        ...new Set(scanRows.map((r) => r.project_name || r.project_display_id || "—").filter(Boolean)),
                      ].map((v) => ({ value: v, label: v })),
                    },
                  },
                  cell: ({ row }) => <span>{row.original.project_name || row.original.project_display_id || "—"}</span>,
                },
                {
                  id: "configured_tools",
                  accessorFn: (r) => (r.configured_tools || r.tools || []).join(", "),
                  header: "Configured Tools",
                  enableSorting: false,
                  cell: ({ row }) => <span>{(row.original.configured_tools || row.original.tools || []).join(", ") || "—"}</span>,
                },
                {
                  id: "executed_tools",
                  header: "Executed Tools (status)",
                  enableSorting: false,
                  // Only real ToolExecution rows count as executed.
                  cell: ({ row }) => (
                    <span>
                      {(executionsByScan[row.original.scan_id] || []).map((e) => `${e.tool_id} (${e.status})`).join(", ") ||
                        "None executed"}
                    </span>
                  ),
                },
              ]}
              data={scanRows}
              getRowId={(r) => String(r.id)}
              emptyMessage="No scans recorded."
              enableColumnVisibility={false}
              enableColumnResizing
              enableColumnFilters
              maxBodyHeight="65vh"
            />
          </div>
        </div>
      )}

      {tab === "retest" && (
        <DataTable
          columns={[
            {
              id: "title",
              accessorFn: (r) => r.title,
              header: "Finding",
              enableSorting: false,
              meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
              cell: ({ row }) => <span>{row.original.title}</span>,
            },
            {
              id: "retestCount",
              accessorFn: (r) => (r.retests || []).length,
              header: "Retest Attempts",
              enableSorting: false,
              cell: ({ row }) => <span>{(row.original.retests || []).length}</span>,
            },
            {
              id: "latest",
              accessorFn: (r) => (r.retests || [])[(r.retests || []).length - 1]?.result || "-",
              header: "Latest Result",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Latest Result",
                  placeholder: "All results",
                  options: [
                    ...new Set(
                      retestRows.map((r) => (r.retests || [])[(r.retests || []).length - 1]?.result).filter(Boolean)
                    ),
                  ].map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => (
                <span>{(row.original.retests || [])[(row.original.retests || []).length - 1]?.result || "-"}</span>
              ),
            },
          ]}
          data={retestRows}
          getRowId={(r) => String(r.id)}
          emptyMessage="No retest history recorded."
          enableColumnVisibility={false}
          enableColumnResizing
          enableColumnFilters
          maxBodyHeight="65vh"
        />
      )}

      {tab === "coverage" && <VaptCoverageExceptionsPanel />}

      {showNotificationPreview && (
        <VaptNotificationPreviewModal
          projectId={reports.records?.[0]?.project_id}
          onClose={() => setShowNotificationPreview(false)}
          onPreviewOnly={() => setNotice("Notification preview reviewed.")}
        />
      )}
    </div>
  );
};

export default VaptReports;
